import { Button, Card, FormInstance, Image, message, Tag, Upload } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import axios from "axios";
import { useState } from "react";
import { getCode } from "country-list";
import dayjs from "dayjs";
interface FaceComparisonProps {
  form: FormInstance<IVisaRequestFormData>;
  matchPercentage: number;
  imageQuality: string;
  t: any;
  setPassportData: (response: any) => void;
  setFaceMatchingData: (response: any) => void;
  passportImage: any;
  setPassportImage: (image: any) => void;
  selfieImage: any;
  setSelfieImage: (image: any) => void;
}

const FaceComparison = ({
  form,
  matchPercentage,
  imageQuality,
  t,
  setPassportData,
  setFaceMatchingData,
  passportImage,
  setPassportImage,
  selfieImage,
  setSelfieImage,
}: FaceComparisonProps) => {
  const [loadingPassport, setLoadingPassport] = useState(false);
  const [loadingSelfie, setLoadingSelfie] = useState(false);

  // Upload props for passport
  const UploadPassportProps = {
    name: "imgPassport",
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      setLoadingPassport(true);
      try {
        // Best-effort OCR: if it fails, user can still continue and fill data manually.
        try {
          const formData = new FormData();
          formData.append("img", file);

          const result = await axios.post(
            "https://demo.computervision.com.vn/api/v3/ekyc/card?format_type=file&get_thumb=true",
            formData,
            {
              auth: {
                username: process.env.REACT_APP_API_KEY || "",
                password: process.env.REACT_APP_API_SECRET || "",
              },
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          const info = result?.data?.data?.info;
          setPassportData(info || null);

          // Update review information (best-effort)
          if (info) {
            const pData = info;
            form.setFieldsValue({
              surname: pData.sur_name,
              middle_and_given_name: pData.given_name,
              date_of_birth: pData.dob
                ? (dayjs(pData.dob, "DD/MM/YYYY") as any)
                : dayjs(),
              gender: pData.gender,
              nationality: getCode(pData.nationality || ""),
              place_of_birth: pData.nationality,
            });
          }
        } catch (ocrErr) {
          console.error("Error performing passport OCR:", ocrErr);
          setPassportData(null);
          message.warning(
            t?.("registration_form.step1.ocr_unavailable") ||
              "Passport OCR is temporarily unavailable. Please fill in passport information manually."
          );
        }

        setPassportImage(file);

        onSuccess("Uploaded successfully");
        message.success(`${file.name} file uploaded successfully`);

        // Handle face matching
        if (selfieImage) {
          try {
            const fmFormData = new FormData();
            fmFormData.append("img1", file);
            fmFormData.append("img2", selfieImage);

            const fmResult = await axios.post(
              "https://demo.computervision.com.vn/api/v3/ekyc/face_matching?type1=card&format_type=file",
              fmFormData,
              {
                auth: {
                  username: process.env.REACT_APP_API_KEY || "",
                  password: process.env.REACT_APP_API_SECRET || "",
                },
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );
            setFaceMatchingData(fmResult.data.data);
          } catch (faceMatchingErr) {
            console.error("Error performing face matching:", faceMatchingErr);
            setFaceMatchingData(null);
            message.warning(
              t?.("face_matching.unavailable", { ns: "message" }) ||
                "Face matching is temporarily unavailable. You can continue without it."
            );
          }
        }
      } catch (err) {
        console.error("Error uploading passport:", err);
        onError(err);
        message.error(`${file.name} file upload failed.`);
      } finally {
        setLoadingPassport(false);
      }
    },
  };
  // Upload props for selfie
  const UploadSelfieProps = {
    name: "imgSelfie",
    customRequest: async (options: any) => {
      const { file, onSuccess, onError } = options;
      setLoadingSelfie(true);
      try {
        const formData = new FormData();

        formData.append("img1", passportImage); // Append the passport image as a file
        formData.append("img2", file); // Append the selfie image
        try {
          const result = await axios.post(
            "https://demo.computervision.com.vn/api/v3/ekyc/face_matching?type1=card&format_type=file",
            formData,
            {
              auth: {
                username: process.env.REACT_APP_API_KEY || "",
                password: process.env.REACT_APP_API_SECRET || "",
              },
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );

          setFaceMatchingData(result.data.data);
        } catch (faceMatchingErr) {
          console.error(
            "Error uploading selfie (face matching):",
            faceMatchingErr
          );
          setFaceMatchingData(null);
          message.warning(
            t?.("face_matching.unavailable", { ns: "message" }) ||
              "Face matching is temporarily unavailable. You can continue without it."
          );
        }

        // Always keep the selfie image so user can continue even if API fails.
        setSelfieImage(file);

        onSuccess("Uploaded successfully");
        message.success(`${file.name} file uploaded successfully`);
      } catch (err) {
        console.error("Error uploading selfie:", err);
        onError(err);
        message.error(`${file.name} file upload failed.`);
      } finally {
        setLoadingSelfie(false);
      }
    },
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 w-full">
      <Card title={t("registration_form.step1.passport")} className="flex-1">
        <div className="flex flex-col gap-4 items-center justify-between">
          <Image
            src={passportImage ? URL.createObjectURL(passportImage) : ""}
            alt="Passport"
            className="min-w-[300px]"
          />
          <Upload
            {...UploadPassportProps}
            accept="image/*"
            beforeUpload={(file) => {
              const isImage = file.type.startsWith("image/");
              if (!isImage) {
                message.error("You can only upload image files!");
              }
              return isImage || Upload.LIST_IGNORE;
            }}
            showUploadList={false}
          >
            <Button loading={loadingPassport}>{t("cta.change")}</Button>
          </Upload>
        </div>
      </Card>

      <Card title={t("registration_form.step1.selfie")} className="flex-1">
        <div className="flex flex-col items-center gap-4">
          <Image
            src={selfieImage ? URL.createObjectURL(selfieImage) : ""}
            alt="Selfie"
            className="max-w-[300px]"
            preview={false}
          />
          <Upload
            {...UploadSelfieProps}
            accept="image/*"
            beforeUpload={(file) => {
              const isImage = file.type.startsWith("image/");
              if (!isImage) {
                message.error("You can only upload image files!");
              }
              return isImage || Upload.LIST_IGNORE;
            }}
            showUploadList={false}
          >
            <Button loading={loadingSelfie}>{t("cta.change")}</Button>
          </Upload>

          <div className="w-full">
            <div className="flex justify-between items-center mb-2">
              <span>
                {t("registration_form.review_profile.section1_face_matched")}
              </span>
              <span className="font-semibold">{matchPercentage}%</span>
            </div>
            <div className="mt-4 flex justify-between items-center gap-2 mb-2">
              <span>
                {t("registration_form.review_profile.section1_image_quality")}
              </span>
              <Tag
                color={imageQuality === "Pass" ? "success" : "error"}
                icon={
                  imageQuality === "Pass" ? (
                    <CheckCircleOutlined />
                  ) : (
                    <CloseCircleOutlined />
                  )
                }
              >
                {imageQuality === "Pass"
                  ? t("registration_form.review_profile.section1_image_pass")
                  : t("registration_form.review_profile.section1_image_fail")}
              </Tag>
            </div>

            {imageQuality !== "Pass" && (
              <div className="mt-4 flex justify-center items-center">
                <Tag color={"error"}>
                  {t("face_matching.error", {
                    ns: "message",
                  })}
                </Tag>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
export default FaceComparison;
